import { useQuery } from "@tanstack/react-query";
import { analysisDocumentsApi } from "@/api/analysis-api";
import { analysisIcon } from "@/components/deal-detail/analysis/analysis-icons";
import { isAgentReportComplete } from "@/components/deal-detail/analysis/agent-report";

/**
 * The Reports tab's view of what the deal's agents have produced — one
 * "Completed" row per finished report, alongside the DGPT reports.
 *
 * Reads the same ["analysis-documents", dealId] query the Agents tab does, so
 * the two never disagree and opening either one warms the other. Opening a row
 * hands off to the Agents tab, which is where the report is edited and exported.
 */
export function AgentReportRows({
  dealId,
  onOpen,
}: {
  dealId: string;
  onOpen?: (templateId: string) => void;
}) {
  const { data } = useQuery({
    queryKey: ["analysis-documents", dealId],
    queryFn: () => analysisDocumentsApi.list(dealId),
    enabled: Boolean(dealId),
  });

  const completed = (data?.documents ?? [])
    .filter(isAgentReportComplete)
    .sort(
      (a, b) =>
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    );

  return (
    <>
      {completed.map((document) => {
        const Icon = analysisIcon(document.template_icon);
        return (
          <div
            key={document._id}
            className="rounded-lg border border-border bg-card"
          >
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                  <p className="text-sm font-medium text-foreground">
                    {document.template_name}
                  </p>
                  <span className="text-[11px] text-muted-foreground/50">
                    Agent
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Completed
                </p>
              </div>

              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  className="rounded-lg border border-border px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-foreground/[0.04] hover:text-foreground"
                  onClick={() => onOpen?.(document.template_id)}
                >
                  Open
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
}
